import React, { useEffect, useState, useRef, useCallback } from "react";
import { Undo2, CheckCircle2 } from "lucide-react";
import { undoAction } from "../../utils/API";

interface UndoButtonProps {
  actionLogId: string;
  collectionName: string;
  operation: string;
  undoWindowSeconds: number;
  onUndoed?: (actionLogId: string) => void;
}

const UndoButton: React.FC<UndoButtonProps> = ({
  actionLogId,
  collectionName,
  operation,
  undoWindowSeconds,
  onUndoed,
}) => {
  const [remaining, setRemaining] = useState(undoWindowSeconds);
  const [undoing, setUndoing] = useState(false);
  const [undone, setUndone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const endTimeRef = useRef<number>(Date.now() + undoWindowSeconds * 1000);

  // Countdown timer
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      const secsLeft = Math.max(0, Math.ceil((endTimeRef.current - Date.now()) / 1000));
      setRemaining(secsLeft);
      if (secsLeft <= 0) {
        if (intervalRef.current) clearInterval(intervalRef.current);
      }
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [undoWindowSeconds]);

  const handleUndo = useCallback(async () => {
    setUndoing(true);
    setError(null);
    try {
      const res = await undoAction(actionLogId);
      if (res?.success === false) {
        setError(res.error || "Undo failed");
        setUndoing(false);
        return;
      }
      setUndone(true);
      onUndoed?.(actionLogId);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Undo failed");
      setUndoing(false);
    }
  }, [actionLogId, onUndoed]);

  // Hide after undo window expires
  if (remaining <= 0 && !undone) return null;

  if (undone) {
    return (
      <div className="inline-flex items-center gap-1.5 text-xs text-emerald-600 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-lg mt-1">
        <CheckCircle2 size={12} />
        <span className="font-medium">Undone</span>
      </div>
    );
  }

  return (
    <div className="mt-1.5">
      {error && (
        <p className="text-[10px] text-red-500 mb-1">{error}</p>
      )}
      <button
        onClick={handleUndo}
        disabled={undoing}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-[#0C447C] bg-white border border-gray-200 hover:border-[#0C447C] px-2.5 py-1 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label={`Undo ${operation} on ${collectionName}`}
      >
        {undoing ? (
          <>
            <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span>Undoing…</span>
          </>
        ) : (
          <>
            <Undo2 size={12} />
            <span>↩ Undo</span>
            <span className="text-gray-400 font-normal">({remaining}s)</span>
          </>
        )}
      </button>
    </div>
  );
};

export default UndoButton;